import { useState, useEffect, useCallback } from "react";
import { docsClient, authClient } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Play,
  History,
  Clock,
  Info,
  RefreshCw,
  Wrench,
  Search,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

// Current algorithm version (should match process-document/index.ts)
const CURRENT_ALGORITHM_VERSION = "2.0.0";

interface QualityIssue {
  document_id: string;
  url: string;
  title: string | null;
  issues: string[];
  severity: "high" | "medium" | "low";
}

interface QualityReport {
  total_documents: number;
  documents_with_issues: number;
  issues_by_severity: {
    high: number;
    medium: number;
    low: number;
  };
  issues: QualityIssue[];
  stats: {
    missing_title: number;
    missing_description: number;
    insufficient_tags: number;
    short_description: number;
    no_content: number;
    failed_processing: number;
  };
}

interface MaintenanceLog {
  id: string;
  user_id: string | null;
  task_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  report: QualityReport | null;
  error_message: string | null;
  created_at: string;
}

interface RecleanPreview {
  id: string;
  title: string | null;
  url: string;
  current_status: string;
  current_size: number;
  current_tokens: number;
  last_cleaned_at: string | null;
  algorithm_version: string | null;
}

export default function MaintenancePage() {
  const [loading, setLoading] = useState(false);
  const [fixingIssues, setFixingIssues] = useState(false);
  const [report, setReport] = useState<QualityReport | null>(null);
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [selectedLog, setSelectedLog] = useState<MaintenanceLog | null>(null);

  // Re-clean state
  const [recleanDaysOld, setRecleanDaysOld] = useState<string>("30");
  const [recleanAlgorithmVersion, setRecleanAlgorithmVersion] =
    useState<string>(CURRENT_ALGORITHM_VERSION);
  const [recleanPreview, setRecleanPreview] = useState<RecleanPreview[] | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [startingReclean, setStartingReclean] = useState(false);

  const { toast } = useToast();

  const loadLogs = useCallback(async () => {
    try {
      setLoadingLogs(true);
      const { data, error } = await docsClient
        .from("maintenance_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error("Error loading logs:", error);
      toast({
        title: "Error",
        description: "Failed to load maintenance history",
        variant: "destructive",
      });
    } finally {
      setLoadingLogs(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const runQualityCheck = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await authClient.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/check-metadata-quality`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        }
      );

      if (!response.ok) throw new Error("Failed to run quality check");

      const data = await response.json();
      setReport(data.report);

      toast({
        title: "Quality Check Complete",
        description: `Found ${data.report.documents_with_issues} documents with issues out of ${data.report.total_documents} total`,
      });

      loadLogs();
    } catch (error) {
      console.error("Error running quality check:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to run quality check",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const runAutoFix = async () => {
    setFixingIssues(true);
    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await authClient.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/auto-fix-quality-issues`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        }
      );

      if (!response.ok) throw new Error("Failed to run auto-fix");

      const data = await response.json();

      toast({
        title: "Auto-Fix Complete",
        description: `Fixed ${data.total_fixed} documents`,
      });

      loadLogs();
    } catch (error) {
      console.error("Error running auto-fix:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to run auto-fix",
        variant: "destructive",
      });
    } finally {
      setFixingIssues(false);
    }
  };

  const runRecleanPreview = async () => {
    setLoadingPreview(true);
    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await authClient.auth.getSession();

      const daysOld = parseInt(recleanDaysOld || "30");
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/reclean-documents`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            filters: {
              processed_before: cutoffDate.toISOString(),
              algorithm_version_below: recleanAlgorithmVersion,
            },
            limit: 100,
            dry_run: true,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to preview documents");

      const data = await response.json();
      setRecleanPreview(data.documents);

      toast({
        title: "Preview Ready",
        description: `Found ${data.document_count} documents to re-clean`,
      });
    } catch (error) {
      console.error("Error previewing re-clean:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to preview documents",
        variant: "destructive",
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const startReclean = async () => {
    setStartingReclean(true);
    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const {
        data: { session },
      } = await authClient.auth.getSession();

      const daysOld = parseInt(recleanDaysOld || "30");
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/reclean-documents`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            filters: {
              processed_before: cutoffDate.toISOString(),
              algorithm_version_below: recleanAlgorithmVersion,
            },
            limit: 100,
            dry_run: false,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to start re-clean");

      const data = await response.json();

      toast({
        title: "Re-clean Started",
        description: `Processing ${data.total_documents} documents`,
      });

      setRecleanPreview(null);
      setTimeout(() => loadLogs(), 1000);
    } catch (error) {
      console.error("Error starting re-clean:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to start re-clean",
        variant: "destructive",
      });
    } finally {
      setStartingReclean(false);
    }
  };

  const getSeverityBadge = (severity: "high" | "medium" | "low") => {
    const config = {
      high: { className: "border-red-500 text-red-700 dark:text-red-400", label: "High" },
      medium: { className: "border-amber-500 text-amber-700 dark:text-amber-400", label: "Medium" },
      low: { className: "border-blue-500 text-blue-700 dark:text-blue-400", label: "Low" },
    };
    return (
      <Badge variant="outline" className={config[severity].className}>
        {config[severity].label}
      </Badge>
    );
  };

  const getTaskTypeName = (taskType: string) => {
    const names: Record<string, string> = {
      quality_check: "Quality Check",
      auto_fix: "Auto-Fix Metadata",
      reclean: "Re-clean Documents",
    };
    return names[taskType] || taskType;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "running":
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Running
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="outline" className="border-red-500 text-red-700 dark:text-red-400">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Maintenance</h1>
        <p className="text-muted-foreground mt-2">
          Keep your documentation library clean and well-organized
        </p>
      </div>

      <Tabs defaultValue="tasks" className="space-y-6">
        <TabsList>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-6">
          {/* Task Cards Grid */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* 1. Quality Check Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-500" />
                  <CardTitle>Quality Check</CardTitle>
                </div>
                <CardDescription>
                  Scan documents for metadata issues like missing titles, descriptions, or
                  insufficient tags
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium text-red-600">High:</span> Missing content, failed
                    processing
                  </p>
                  <p>
                    <span className="font-medium text-amber-600">Medium:</span> Missing
                    title/description, &lt;3 tags
                  </p>
                  <p>
                    <span className="font-medium text-blue-600">Low:</span> Short description
                    (&lt;20 chars)
                  </p>
                </div>
                <Button onClick={runQualityCheck} disabled={loading} className="w-full">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run Quality Check
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* 2. Auto-Fix Metadata Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-green-500" />
                  <CardTitle>Auto-Fix Metadata</CardTitle>
                </div>
                <CardDescription>
                  Automatically repair missing titles, descriptions, and tags without re-fetching
                  content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium">Titles:</span> Generated from document URL
                  </p>
                  <p>
                    <span className="font-medium">Descriptions:</span> Extracted from content
                  </p>
                  <p>
                    <span className="font-medium">Tags:</span> Auto-generated from URL, domain,
                    title
                  </p>
                </div>
                <Button
                  onClick={runAutoFix}
                  disabled={fixingIssues}
                  variant="default"
                  className="w-full"
                >
                  {fixingIssues ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Fixing...
                    </>
                  ) : (
                    <>
                      <Wrench className="mr-2 h-4 w-4" />
                      Run Auto-Fix
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* 3. Re-clean Documents Card (Full Width) */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-orange-500" />
                <CardTitle>Re-clean Documents</CardTitle>
              </div>
              <CardDescription>
                Re-fetch and re-process documents with the latest content extraction algorithm
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Algorithm Version Info */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Algorithm Versions</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm space-y-1">
                    <p>
                      Current version: <strong>{CURRENT_ALGORITHM_VERSION}</strong>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      <strong>v1.0.0:</strong> Basic HTML cleaning, simple text extraction
                    </p>
                    <p className="text-muted-foreground text-xs">
                      <strong>v2.0.0:</strong> Enhanced code block handling, improved markdown
                      conversion, smarter extraction
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="reclean-days">Documents older than (days)</Label>
                  <Input
                    id="reclean-days"
                    type="number"
                    min="1"
                    value={recleanDaysOld}
                    onChange={(e) => setRecleanDaysOld(e.target.value)}
                    placeholder="30"
                  />
                  <p className="text-xs text-muted-foreground">
                    Only re-clean documents processed more than this many days ago
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reclean-version">Algorithm version below</Label>
                  <Input
                    id="reclean-version"
                    type="text"
                    value={recleanAlgorithmVersion}
                    onChange={(e) => setRecleanAlgorithmVersion(e.target.value)}
                    placeholder={CURRENT_ALGORITHM_VERSION}
                  />
                  <p className="text-xs text-muted-foreground">
                    Re-clean documents processed with versions older than this
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={runRecleanPreview} disabled={loadingPreview} variant="outline">
                  {loadingPreview ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Preview Documents
                    </>
                  )}
                </Button>
                {recleanPreview && recleanPreview.length > 0 && (
                  <Button onClick={startReclean} disabled={startingReclean}>
                    {startingReclean ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Start Re-clean ({recleanPreview.length} docs)
                      </>
                    )}
                  </Button>
                )}
              </div>

              {recleanPreview && recleanPreview.length === 0 && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertTitle>All Up to Date</AlertTitle>
                  <AlertDescription>
                    No documents need re-cleaning with the current filters.
                  </AlertDescription>
                </Alert>
              )}

              {recleanPreview && recleanPreview.length > 0 && (
                <div className="border rounded-lg">
                  <div className="p-3 border-b bg-muted/50">
                    <p className="font-medium text-sm">
                      Preview: {recleanPreview.length} documents to re-clean
                    </p>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 space-y-2">
                    {recleanPreview.slice(0, 10).map((doc) => (
                      <div key={doc.id} className="border rounded p-2 text-sm">
                        <p className="font-medium truncate">
                          {doc.title || <span className="text-muted-foreground">Untitled</span>}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{doc.url}</p>
                        <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                          <span>Version: {doc.algorithm_version || "1.0.0"}</span>
                          <span>Tokens: {doc.current_tokens?.toLocaleString() || "N/A"}</span>
                        </div>
                      </div>
                    ))}
                    {recleanPreview.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        ... and {recleanPreview.length - 10} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quality Report Results */}
          {report && (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.total_documents}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      With Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.documents_with_issues}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      High Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {report.issues_by_severity.high}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Medium Priority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-600">
                      {report.issues_by_severity.medium}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {report.issues.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Documents with Issues</CardTitle>
                    <CardDescription>
                      Run Auto-Fix to repair metadata issues automatically
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Severity</TableHead>
                          <TableHead>Document</TableHead>
                          <TableHead>Issues</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.issues.slice(0, 20).map((issue) => (
                          <TableRow key={issue.document_id}>
                            <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                            <TableCell>
                              <div className="max-w-md">
                                <p className="font-medium truncate">
                                  {issue.title || (
                                    <span className="text-muted-foreground">Untitled</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {issue.url}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {issue.issues.map((text, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {text}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {report.issues.length > 20 && (
                      <p className="text-sm text-muted-foreground text-center mt-4">
                        Showing 20 of {report.issues.length} documents
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {report.documents_with_issues === 0 && (
                <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800 dark:text-green-200">All Clear!</AlertTitle>
                  <AlertDescription className="text-green-700 dark:text-green-300">
                    All {report.total_documents} documents have complete metadata.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance History</CardTitle>
              <CardDescription>View past maintenance task executions and results</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No maintenance tasks have been run yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Summary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const duration = log.completed_at
                        ? Math.round(
                            (new Date(log.completed_at).getTime() -
                              new Date(log.started_at).getTime()) /
                              1000
                          )
                        : null;

                      return (
                        <TableRow
                          key={log.id}
                          className={
                            log.status === "completed" && log.report
                              ? "cursor-pointer hover:bg-muted/50"
                              : ""
                          }
                          onClick={() => {
                            if (log.status === "completed" && log.report) {
                              setSelectedLog(log);
                              setReport(log.report as QualityReport);
                            }
                          }}
                        >
                          <TableCell className="font-medium">
                            {getTaskTypeName(log.task_type)}
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(log.started_at), { addSuffix: true })}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {duration ? `${duration}s` : "-"}
                          </TableCell>
                          <TableCell>
                            {log.status === "completed" && log.report && (
                              <span className="text-sm">
                                {log.report.documents_with_issues} issues in{" "}
                                {log.report.total_documents} docs
                              </span>
                            )}
                            {log.status === "failed" && (
                              <span className="text-sm text-destructive">
                                {log.error_message || "Failed"}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {selectedLog && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Click a completed task above to view its report
              </p>
              <Button variant="ghost" size="sm" onClick={() => setSelectedLog(null)}>
                Clear Selection
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
